// Workspaces que operam no modo Enterprise. É uma LISTA (não um slug único)
// porque a "matriz" e futuras réplicas compartilham o comportamento Enterprise
// no front — só o slug muda (slug é UNIQUE na tabela workspaces).
export const ENTERPRISE_WORKSPACE_SLUGS = ['enterprise', 'enterprise-matriz', 'aex'] as const;

// Slug "principal" — mantido por compatibilidade com quem importava o singular.
export const ENTERPRISE_WORKSPACE_SLUG = ENTERPRISE_WORKSPACE_SLUGS[0];

// O workspace dos parceiros AEX. É Enterprise (está na lista acima), mas tem
// regras próprias que NÃO valem para os demais — hoje, o handoff ao Comercial
// sem agendamento (ver @config/aexHandoff).
export const AEX_WORKSPACE_SLUG = 'aex';
export const ENTERPRISE_GOALS_CONFIG_KEY = 'enterprise_goals';

export interface EnterpriseGoalsConfig {
  individual: {
    goal: number;
    superGoal: number;
    triumph: number;
  };
  collective: {
    goal: number;
    superGoal: number;
  };
}

export const DEFAULT_ENTERPRISE_GOALS: EnterpriseGoalsConfig = {
  individual: {
    goal: 125,
    superGoal: 250,
    triumph: 500,
  },
  collective: {
    goal: 2000,
    superGoal: 3750,
  },
};

export const isEnterpriseWorkspace = (workspace?: unknown): boolean => {
  const row = workspace && typeof workspace === 'object'
    ? workspace as { slug?: unknown; settings?: { crm_mode?: unknown } | null }
    : null;
  if (String(row?.settings?.crm_mode || '').toLowerCase() === 'enterprise') return true;
  const slug = row?.slug;
  const normalized = String(slug || '').toLowerCase();
  return (ENTERPRISE_WORKSPACE_SLUGS as readonly string[]).includes(normalized);
};

// Só o workspace AEX (não os demais Enterprise). Diferente de
// `isEnterpriseWorkspace`, NÃO aceita `settings.crm_mode` — o modo Enterprise
// é compartilhado, o slug é que identifica esta operação.
export const isAexWorkspace = (workspace?: unknown): boolean => {
  const row = workspace && typeof workspace === 'object'
    ? workspace as { slug?: unknown }
    : null;
  return String(row?.slug || '').toLowerCase() === AEX_WORKSPACE_SLUG;
};

export const enterpriseRoleLabel = (role: string | null | undefined): string => {
  if (role === 'aex') return 'AEX';
  if (role === 'sdr') return 'Colaborador';
  if (role === 'closer') return 'Comercial';
  if (role === 'gestor') return 'Gestor';
  return String(role || '');
};

// Mesma tradução, mas a partir do modo já resolvido. Quem só tem o booleano
// (e não o objeto do workspace) evita depender da identidade dele — o
// `useAuth` recria o objeto a cada refresh de token, e um useMemo que
// dependa dele reprocessa sem nada ter mudado.
export const roleLabelForMode = (
  role: string | null | undefined,
  enterprise: boolean,
): string => {
  if (enterprise) return enterpriseRoleLabel(role);
  if (role === 'aex') return 'AEX';
  if (role === 'sdr') return 'SDR';
  if (role === 'closer') return 'Closer';
  if (role === 'gestor') return 'Gestor';
  return String(role || '');
};

export const workspaceRoleLabel = (
  role: string | null | undefined,
  workspace?: unknown,
): string => roleLabelForMode(role, isEnterpriseWorkspace(workspace));

export const enterpriseText = (text: string, workspace?: unknown): string => {
  if (!isEnterpriseWorkspace(workspace)) return text;
  return text
    .replace(/\bSDR\b/g, 'Colaborador')
    .replace(/\bCloser\b/g, 'Comercial')
    .replace(/\bcloser\b/g, 'comercial');
};

export const normalizeEnterpriseGoals = (value: unknown): EnterpriseGoalsConfig => {
  const source = value && typeof value === 'object'
    ? value as Partial<EnterpriseGoalsConfig>
    : {};
  return {
    individual: {
      goal: Number(source.individual?.goal) || DEFAULT_ENTERPRISE_GOALS.individual.goal,
      superGoal: Number(source.individual?.superGoal) || DEFAULT_ENTERPRISE_GOALS.individual.superGoal,
      triumph: Number(source.individual?.triumph) || DEFAULT_ENTERPRISE_GOALS.individual.triumph,
    },
    collective: {
      goal: Number(source.collective?.goal) || DEFAULT_ENTERPRISE_GOALS.collective.goal,
      superGoal: Number(source.collective?.superGoal) || DEFAULT_ENTERPRISE_GOALS.collective.superGoal,
    },
  };
};
